import {
  useModalOldContext,
  WSButton,
  WSButtons,
  WSDivider,
  WSFlexBox,
  WSInfoBox,
  WSPanel,
  WSPill,
  WSSidebar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Deduction,
  DeductionApplicationType
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Currency } from "@wingspanhq/utils/dist/currency/currencies";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { WSQueries } from "../../../query/WSQuery";
import { queriesDeductions } from "@query";
import { goBackOrToParent } from "../../../utils/goToParentRoute";
import { DeductionsHistoryList } from "../../components/DeductionsHistoryList/DeductionsHistoryList";
import { DELETE_DEDUCTION_MODAL } from "./modals/DeleteDeduction";

export const ScheduledDeductionDetails: React.FC<
  RouteComponentProps<{
    deductionId: string;
  }>
> = ({ match, location }) => {
  const history = useHistory();
  const qDeduction = queriesDeductions.getDeduction.useQuery({
    deductionId: match.params.deductionId
  });

  const onClose = () => goBackOrToParent(history);

  return (
    <WSSidebar onClose={onClose} header="Scheduled deduction details">
      <WSQueries queries={{ qDeduction }} renderErrors={() => null}>
        {({ qDeduction: { data: deduction } }) => <Inner data={deduction} />}
      </WSQueries>
    </WSSidebar>
  );
};

export const Inner: React.FC<{ data: Deduction }> = ({ data }) => {
  const history = useHistory();
  const { openModal } = useModalOldContext();

  // v3 types `Deduction.currency` as a bare string (unlike `PayableRow`, which
  // uses the `Currency` enum), so it is narrowed once here rather than at each
  // of the `formatMoney` calls below.
  const currency = (data.currency ?? Currency.USD) as Currency;
  const amount = data.amount ?? 0;

  const deductedAmount = (data.application ?? [])
    .map(a => a.amountDeducted ?? 0)
    .reduce((a, b) => a + b, 0);

  const remainingAmount = deductedAmount ? amount - deductedAmount : amount;

  return (
    <>
      <WSPanel noBorder mb="XL" data-testid="deductionsPanel">
        <WSFlexBox.CenterY mt="M" mb="XL" alignItems="flex-end">
          {remainingAmount !== amount ? (
            <>
              <WSText.Heading5 mr="XS">
                {formatMoney(remainingAmount, currency)}
              </WSText.Heading5>
              <WSText.ParagraphSm color="gray400">
                remaining of
                {` ${formatMoney(amount, currency)} `}
                total
              </WSText.ParagraphSm>
            </>
          ) : (
            <WSText.Heading5>{formatMoney(amount, currency)}</WSText.Heading5>
          )}
        </WSFlexBox.CenterY>
        <WSFlexBox.CenterY justify="space-between" my="M">
          <WSText weight="medium">{data.name}</WSText>
          <WSPill
            icon={
              data.applicationType === DeductionApplicationType.POST_PAYMENT
                ? "after"
                : "before"
            }
            text={
              data.applicationType === DeductionApplicationType.POST_PAYMENT
                ? "Post-payment"
                : "Pre-payment"
            }
          />
        </WSFlexBox.CenterY>

        {data.detail ? <WSInfoBox mb="L">{data.detail}</WSInfoBox> : null}

        <WSDivider />
        <WSFlexBox.CenterY justify="space-between" my="M">
          <WSText.ParagraphSm>Total scheduled deduction</WSText.ParagraphSm>
          <WSText.ParagraphSm>
            {formatMoney(amount, currency)}
          </WSText.ParagraphSm>
        </WSFlexBox.CenterY>
        <WSDivider />
        <WSFlexBox.CenterY justify="space-between" my="M">
          <WSText.ParagraphSm>Already deducted</WSText.ParagraphSm>
          <WSText.ParagraphSm>
            {formatMoney(deductedAmount, currency)}
          </WSText.ParagraphSm>
        </WSFlexBox.CenterY>

        <WSDivider />
        <WSFlexBox.CenterY justify="space-between" my="M">
          <WSText weight="medium">Remaining balance</WSText>
          <WSText weight="medium">
            {formatMoney(remainingAmount, currency)}
          </WSText>
        </WSFlexBox.CenterY>

        <DeductionsHistoryList deduction={data} />

        <WSButtons forceFullWidth mt="XL">
          <WSButton
            destructive
            onAsyncClick={async () =>
              openModal(DELETE_DEDUCTION_MODAL, { deduction: data })
            }
          >
            Delete deduction
          </WSButton>
        </WSButtons>
      </WSPanel>
    </>
  );
};
